import { normalizeIesire } from "./dirtyState";
import InvoiceAnnex from "../contor/InvoiceAnnex";
import CostAllocation from "../nc/CostAllocation";
// src/components/iesire/Edit.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Alert, Spinner, Button, Modal, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { authFetch } from "../utils/authFetch";
import IesireForm from "./Form";
import DocumentToolbar from "../common/DocumentToolbar";
import useDocumentClosing from "../../hooks/useDocumentClosing";
import { useAuth } from "../auth/AuthContext";

import HelpModal from "../common/HelpModal";
import TreeHelp from "../common/TreeHelp";
import { useHelpHotkey } from "../../hooks/useHelpHotkey";
import ViewNotaModal from "../ViewNotaModal";
import DetaliiScazutModal from "./DetaliiScazutModal"; // ❗ vezi observatia de mai sus

async function getDoc(id) {
  return await authFetch(`/iesire/${id}`);
}

async function putDoc(id, payload, revision) {
  return await authFetch(`/iesire/${id}`, {
    method: "PUT",
    headers: { "If-Match": revision },
    body: JSON.stringify(payload),
  });
}

// normalize pentru dirty: scoate campuri volatile (ajusteaza dupa structura ta reala)
// Shared comparison of editable fields only.
const hash = (obj) => JSON.stringify(obj);

export default function IesireEdit() {
  const saveInFlight = useRef(false);
  const { id } = useParams();
  const nav = useNavigate();

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  const { user } = useAuth();
  const closingEnabled = useDocumentClosing();
  const isFinal = !!doc?.bxfinal_bool;
  const isClosed = isFinal;
  const [showAnnex,setShowAnnex]=useState(false);
  const [contorDetails,setContorDetails]=useState(null);
  const [showNota, setShowNota] = useState(false);
  const [showDetalii, setShowDetalii] = useState(false);

  const [initialForm, setInitialForm] = useState(null);
  const [form, setForm] = useState(null);

  const { showHelp, setShowHelp } = useHelpHotkey();
  const [loadingDoc, setLoadingDoc] = useState(true);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({ to_email: "", subject: "", body: "" });
  const [emailSending, setEmailSending] = useState(false);

  const openEmailModal = () => {
    const clientEmail = doc?.contact_email || "";
    const docNo = doc?.doc_no || id;
    setEmailData({
      to_email: clientEmail,
      subject: `Factura ${docNo}`,
      body: `Buna ziua,\n\nVa transmitem atasat factura cu numarul ${docNo}.\n\nO zi placuta!`,
    });
    setShowEmailModal(true);
  };

  const handleSendEmail = async () => {
    if (!emailData.to_email) {
      toast.error("Adresa de email este obligatorie!");
      return;
    }
    setEmailSending(true);
    try {
      await authFetch(`/iesire/${id}/send_email`, {
        method: "POST",
        body: JSON.stringify(emailData)
      });
      toast.success("Email trimis cu succes!");
      setShowEmailModal(false);
    } catch(e) {
      toast.error(`Eroare trimitere email: ${e.message}`);
    } finally {
      setEmailSending(false);
    }
  };

  useEffect(() => {
    let alive = true;
    setErr("");
    setLoading(true);

    getDoc(id)
      .then((d) => {
        if (!alive) return;
        setDoc(d);
        setInitialForm(d);
        setForm(d);
      })
      .catch((e) => {
        if (!alive) return;
        setErr(String(e?.message || e));
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [id]);

  const originalHash = useMemo(
    () => hash(normalizeIesire(initialForm)),
    [initialForm],
  );
  const currentHash = useMemo(() => hash(normalizeIesire(form)), [form]);
  const isDirty = originalHash !== currentHash;


  const handleSubmit = async (payload) => {
    if (saveInFlight.current) return;
    saveInFlight.current = true;
    setErr("");
    setBusy(true);
    try {
      const updated = await putDoc(id, payload, initialForm?.revision);
      const nextDoc = updated || doc;

      setDoc(nextDoc);
      setInitialForm(nextDoc);
      setForm(nextDoc);

      toast.success("Iesirea a fost salvata cu succes!");
      nav(`/iesire/${id}`);
      return nextDoc;
    } catch (e) {
      setErr(String(e?.message || e));
      toast.error(`Eroare la salvare: ${e?.message || e}`);
      throw e;
    } finally {
      saveInFlight.current = false;
      setBusy(false);
    }
  };

  const changeFinalization = async (finalize) => {
    if (!window.confirm(finalize ? "Finalizezi documentul? Nu va mai putea fi modificat sau șters." : "Redeschizi documentul pentru modificări?")) return;
    setBusy(true);
    try {
      await authFetch(`/iesire/${id}/${finalize ? "finalize" : "unfinalize"}`, { method: "POST" });
      const fresh = await getDoc(id);
      setDoc(fresh);
      setForm(fresh);
      setInitialForm(fresh);
      toast.success(finalize ? "Document finalizat." : "Document redeschis.");
    } catch (e) {
      toast.error(e.message || "Nu am putut schimba starea documentului.");
    } finally { setBusy(false); }
  };

  return (
    <Container className="py-3">
      <DocumentToolbar
        title={`Ieșire #${id}`} dirty={isDirty} error={err}
        isFinal={isFinal} busy={busy || loading} closingEnabled={closingEnabled}
        onBack={() => doc?.deviz_id ? nav("/comenzi/intern/linii") : doc?.cont_id ? nav(`/iesire/iesire_list/${doc.cont_id}`) : nav(-1)}
        addLabel="Adaugă ieșire"
        onAdd={() => nav(`/iesire/new?cont_id=${doc?.cont_id || ""}&bxsoldint=${doc?.bxsoldint || 0}`)}
        onSave={() => window.dispatchEvent(new Event("iesire-submit"))} saveDisabled={!isDirty}
        onFinalize={() => changeFinalization(true)} finalizeDisabled={isDirty}
        canReopen={user?.is_superuser || user?.user_name === "admin" || user?.perms?.includes("IESIRE_VALIDATE")}
        onReopen={() => changeFinalization(false)}
        actions={[
          ...(doc?.contor_generated?[{label:"Scadență, proiect și observații",disabled:busy||isDirty,onClick:()=>setContorDetails({bxdatascadenta:doc.bxdatascadenta?.slice(0,10)||"",proiect_client:doc.proiect_client||"",observatii:doc.observatii||""})}]:[]),
          { label: "Anexă cu detalii", onClick: () => setShowAnnex(true), disabled: !doc },
          { label: "Tipărește", onClick: () => window.open(`/iesire/${id}/print`, "_blank") },
          { label: "Trimite email", onClick: openEmailModal, disabled: !doc },
          { label: "Vezi nota contabilă", onClick: () => setShowNota(true) },
          { label: "Detaliu scădere", onClick: () => setShowDetalii(true) },
          { label: "Ajutor (F1)", onClick: () => setShowHelp(true) },
        ]}
      />

      {err ? <Alert variant="danger">{err}</Alert> : null}

      {loading ? (
        <Spinner animation="border" />
      ) : (
        <IesireForm
          initial={doc}
          showActions={false}
          onSave={handleSubmit}
          busy={busy}
          loadingDoc={loadingDoc}
          isClosed={isClosed}
          onChangePayload={(p) => setForm(p)} // 👈 pentru dirty (adauga in Form daca nu exista)
        />
      )}

      {!loading && doc && isFinal && <CostAllocation recType={5} docId={id} refreshKey={doc} />}
      <Modal show={!!contorDetails} onHide={()=>!busy&&setContorDetails(null)}><Modal.Header closeButton><Modal.Title>Detalii factură CONTOR</Modal.Title></Modal.Header><Modal.Body>
      <p>Puteți actualiza aceste câmpuri fără redeschiderea facturii. Calculul și contoarele se păstrează.</p>
      {err&&<Alert variant="danger">{err}</Alert>}
      {contorDetails&&<Form onSubmit={async e=>{e.preventDefault();if(saveInFlight.current)return;saveInFlight.current=true;setBusy(true);setErr("");try{const fresh=await authFetch('/iesire/'+id+'/detalii-contor',{method:'PATCH',headers:{'If-Match':doc.revision},body:JSON.stringify({...contorDetails,bxdatascadenta:contorDetails.bxdatascadenta+'T00:00:00'})});setDoc(fresh);setInitialForm(fresh);setForm(fresh);setContorDetails(null);toast.success('Detaliile facturii au fost salvate.');}catch(e){setErr(e.message);}finally{saveInFlight.current=false;setBusy(false);}}}><fieldset disabled={busy}>
      <Form.Group controlId="contor-due"><Form.Label>Data scadentă</Form.Label><Form.Control required type="date" min={doc?.doc_date?.slice(0,10)} value={contorDetails.bxdatascadenta} onChange={e=>setContorDetails({...contorDetails,bxdatascadenta:e.target.value})}/></Form.Group>
      <Form.Group controlId="contor-project"><Form.Label>Proiect client</Form.Label><Form.Control maxLength={30} value={contorDetails.proiect_client} onChange={e=>setContorDetails({...contorDetails,proiect_client:e.target.value})}/></Form.Group>
      <Form.Group controlId="contor-notes"><Form.Label>Observații</Form.Label><Form.Control as="textarea" maxLength={200} value={contorDetails.observatii} onChange={e=>setContorDetails({...contorDetails,observatii:e.target.value})}/></Form.Group>
      <Button type="submit" className="mt-3">Salvează detaliile</Button></fieldset></Form>}</Modal.Body></Modal>
      <InvoiceAnnex id={id} show={showAnnex} onHide={()=>setShowAnnex(false)}/>
      <HelpModal
        show={showHelp}
        onHide={() => setShowHelp(false)}
        title="Manual de Utilizare ERP"
      >
        <TreeHelp defaultChapter="iesiri" />
      </HelpModal>

      {/* ❗ daca DetaliiScazutModal e doar pentru bon_consum, fa unul pt iesire */}
      <DetaliiScazutModal
        show={showDetalii}
        onHide={() => setShowDetalii(false)}
        docType="iesire"
        docId={id}
      />

      {/* recType: schimba daca iesire are alt rec_type */}
      <ViewNotaModal
        recType={5}
        docId={id}
        show={showNota}
        onHide={() => setShowNota(false)}
      />

      {/* Email Modal */}
      <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Trimite Factura pe Email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Adresa de Email (Destinatar)</Form.Label>
              <Form.Control
                type="email"
                value={emailData.to_email}
                onChange={(e) => setEmailData({...emailData, to_email: e.target.value})}
                placeholder="exemplu@client.ro"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Subiect</Form.Label>
              <Form.Control
                type="text"
                value={emailData.subject}
                onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Mesaj</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={emailData.body}
                onChange={(e) => setEmailData({...emailData, body: e.target.value})}
              />
            </Form.Group>
            <Alert variant="info" className="mb-0 py-2">
              Factura va fi atasata automat ca PDF.
            </Alert>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEmailModal(false)} disabled={emailSending}>
            Anuleaza
          </Button>
          <Button variant="primary" onClick={handleSendEmail} disabled={emailSending || !emailData.to_email}>
            {emailSending ? <Spinner size="sm" /> : "Trimite"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
